/**
 * levelplate.ts — the label plate shared by every chart primitive that draws a
 * horizontal level.
 *
 * WHY THIS IS ITS OWN MODULE
 * --------------------------
 * Two primitives draw "text on a translucent chip beside a horizontal level, readable
 * over candles": the ticket's draggable legs and the account's order levels. They are
 * the same picture at the same prices, so a copy in each would let their collision
 * behaviour drift apart — and the order levels had no labels at all, which is the
 * defect this module exists to fix: they were drawn with `createPriceLine`, whose
 * `title` only ever appears in the price-axis label, and that label is deliberately
 * off (12 CE levels + 5 range levels stack into a column covering recent price
 * action). So a live entry, stop and take-profit rendered as three unlabelled 1px
 * dotted lines: correct prices, zero indication of which was which.
 *
 * `rangeprimitive.ts` keeps its own label helper on purpose — its text is anchored to
 * the bar that set the level and sits ABOVE the line, not at the pane's left edge.
 *
 * COLLISION HANDLING IS THE POINT
 * -------------------------------
 * Plates are placed in y order and each returns the box it took, so a later plate
 * that would overlap steps RIGHT instead of drawing on top. Measured on the
 * rendered page: without it, four legs a few hundred dollars apart all started at
 * x=6px and only three of four chips could be read. A label you cannot read is a
 * level you cannot verify.
 */

/** A box a plate occupied, in bitmap coordinates. Feed these back as `taken`. */
export interface PlateBox {
  top: number;
  bottom: number;
  right: number;
}

export interface PlateOpts {
  /** Bold text + thicker border: used for the hovered/dragged leg. */
  emphasis?: boolean;
  /** Distance from the left edge for the first plate on a row. */
  inset?: number;
  /**
   * Pane height, enabling the BOTTOM-LEFT WATERMARK DODGE.
   *
   * lightweight-charts paints its "Charting by TradingView" badge in the bottom-left
   * corner, above our canvas. A plate landing there is unreadable — measured on the
   * live xyz:AMZN short, the lowest chip rendered as a bare "…96" with its label
   * buried under the badge, so a real resting order was on the chart and could not be
   * identified. When a plate's y falls inside that corner it starts to the RIGHT of
   * the badge instead.
   */
  paneHeight?: number;
}

/** Bottom-left area the TradingView attribution badge occupies, in CSS px. Measured
 *  from the rendered page rather than guessed: the badge is ~120x32 with a small
 *  margin, so 140x40 clears it without pushing plates needlessly far right. */
const WATERMARK_W = 140;
const WATERMARK_H = 40;

/** Stroke a full-width horizontal rule at `y`. The line half of a labelled level. */
export function drawRule(
  ctx: CanvasRenderingContext2D,
  y: number,
  width: number,
  color: string,
  hr: number,
  { dashed = false, emphasis = false }: { dashed?: boolean; emphasis?: boolean } = {},
): void {
  ctx.save();
  ctx.strokeStyle = color;
  // Thicker under the cursor: the only affordance telling you a level is grabbable
  // before you press it.
  ctx.lineWidth = Math.max(1, Math.round((emphasis ? 2 : 1) * hr));
  if (dashed) ctx.setLineDash([5 * hr, 4 * hr]);
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(width, y);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw one label plate at `y`, stepping right past anything in `taken`.
 *
 * Returns the box it occupied. Callers accumulate those and pass them back so the
 * next plate can avoid them; `taken` must be in y order for the single-pass step
 * to clear every overlap (any box overlapping this one vertically has already been
 * placed, so pushing past the rightmost of them clears all of them).
 */
export function drawPlate(
  ctx: CanvasRenderingContext2D,
  text: string,
  color: string,
  y: number,
  hr: number,
  vr: number,
  taken: PlateBox[],
  opts: PlateOpts = {},
): PlateBox {
  const { emphasis = false, inset = 8, paneHeight } = opts;
  ctx.save();
  ctx.font = `${emphasis ? 'bold ' : ''}${Math.round(10 * vr)}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const padX = 4 * hr;
  const w = ctx.measureText(text).width + padX * 2;
  const h = 14 * vr;
  const top = y - h / 2;
  const bottom = y + h / 2;
  let x = inset * hr;
  // Dodge the attribution badge before the collision pass, so a plate pushed right
  // by the badge is still avoided by its neighbours.
  if (paneHeight !== undefined && bottom > (paneHeight - WATERMARK_H) * vr) {
    x = Math.max(x, WATERMARK_W * hr);
  }
  for (const box of taken) {
    if (top < box.bottom && bottom > box.top) x = Math.max(x, box.right + 3 * hr);
  }
  ctx.fillStyle = 'rgba(19, 23, 34, 0.86)';
  ctx.fillRect(x, top, w, h);
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, Math.floor(hr)) * (emphasis ? 2 : 1);
  ctx.strokeRect(x, top, w, h);
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + padX, y);
  ctx.restore();
  return { top, bottom, right: x + w };
}
